import { apiFetchData, withAuthHeader, type AuthRetry } from "@/shared/lib/api";
import { useAuthStore } from "@/features/auth/store";
import type { UserMeResponse, UpdateUserRequest, CompleteProfileRequest, ConfirmPasswordChangeRequest, OtpResponse } from "./types";

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

/**
 * User/Account API client - all user profile and settings backend calls.
 * Uses auth retry on 401 so token refresh happens without page reload.
 */
export const userApi = {
  /**
   * Get current user profile.
   */
  async getMe(token: string | null): Promise<UserMeResponse> {
    if (!token) throw new Error("No token");
    return apiFetchData<UserMeResponse>("/api/users/me", {
      method: "GET",
      headers: withAuthHeader(undefined, token),
    }, getAuthRetry());
  },

  /**
   * Update current user profile (general settings).
   */
  async updateMe(token: string | null, payload: UpdateUserRequest): Promise<UserMeResponse> {
    if (!token) throw new Error("No token");
    return apiFetchData<UserMeResponse>("/api/auth/me", {
      method: "PATCH",
      headers: withAuthHeader(undefined, token),
      body: payload,
    }, getAuthRetry());
  },

  /**
   * Complete first-time profile setup (name, phone, email) — called after Google/Phone sign-in.
   */
  async completeProfile(token: string, payload: CompleteProfileRequest): Promise<UserMeResponse> {
    return apiFetchData<UserMeResponse>("/api/auth/me", {
      method: "PATCH",
      headers: withAuthHeader(undefined, token),
      body: payload,
    }, getAuthRetry());
  },

  /** Returns true if the email is already registered (public — no token needed). */
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const data = await apiFetchData<{ exists: boolean }>(
        `/api/public/auth/check-email?email=${encodeURIComponent(email)}`,
      );
      return data.exists;
    } catch {
      return false;
    }
  },

  /** Returns true if the phone number is already registered (public — no token needed). */
  async checkPhoneExists(phone: string): Promise<boolean> {
    try {
      const data = await apiFetchData<{ exists: boolean }>(
        `/api/public/auth/check-phone?phone=${encodeURIComponent(phone)}`,
      );
      return data.exists;
    } catch {
      return false;
    }
  },

  /**
   * Given a phone number, returns the email linked to that account (public — no token needed).
   * Used for "phone + password" login: resolve phone → email → signInWithEmailAndPassword.
   */
  async resolveEmailByPhone(phone: string): Promise<string | null> {
    try {
      const data = await apiFetchData<{ email?: string }>('/api/public/auth/resolve-email', {
        method: 'POST',
        body: { phone },
      });
      return data.email ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Request OTP for password change.
   */
  async requestPasswordChangeOtp(token: string | null): Promise<OtpResponse> {
    if (!token) throw new Error("No token");
    return apiFetchData<OtpResponse>("/api/users/me/password/change/request-otp", {
      method: "POST",
      headers: withAuthHeader(undefined, token),
    }, getAuthRetry());
  },

  /**
   * Confirm password change with OTP.
   */
  async confirmPasswordChange(token: string | null, payload: ConfirmPasswordChangeRequest): Promise<void> {
    if (!token) throw new Error("No token");
    return apiFetchData<void>("/api/users/me/password/change/confirm", {
      method: "POST",
      headers: withAuthHeader(undefined, token),
      body: payload,
    }, getAuthRetry());
  },
};

